import type { ChangeStop } from "@ext/git/core/Diff/logic/changeStops";
import collapsedLabel from "@ext/git/core/Diff/logic/collapsedLabel";
import collapsedRanges, { type CollapsedRange, DIFF_ONLY_CHANGES } from "@ext/git/core/Diff/logic/collapsedRanges";
import topLevelBlocks, { blockIndexAt, type TopLevelBlock } from "@ext/git/core/Diff/logic/topLevelBlocks";
import t from "@ext/localization/locale/translate";
import type { Node } from "prosemirror-model";
import { type EditorState, Plugin, PluginKey, Selection, type Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

export const onlyChangesKey = new PluginKey<OnlyChangesState>("diff-only-changes");

const HIDDEN_CLASS = "diff-collapsed-block";
const ZIGZAG_CLASS = "diff-collapsed";
const PREVIEW_LINES = 6;
const PREVIEW_CHARS = 90;

export interface OnlyChangesState {
	enabled: boolean;
	stops: ChangeStop[];
	keepVisible: number[];
	expanded: number[];
	caret: number | null;
	ranges: CollapsedRange[];
	decorations: DecorationSet;
}

export type OnlyChangesMeta =
	| { type: "model"; stops: ChangeStop[]; keepVisible: number[] }
	| { type: "enabled"; value: boolean }
	| { type: "expand"; firstIndex: number };

const shorten = (text: string) => {
	const trimmed = text.trim().replace(/\s+/g, " ");
	return trimmed.length > PREVIEW_CHARS ? `${trimmed.slice(0, PREVIEW_CHARS)}…` : trimmed;
};

const previewOf = (blocks: TopLevelBlock[], range: CollapsedRange): string[] =>
	blocks
		.slice(range.firstIndex, range.lastIndex + 1)
		.slice(0, PREVIEW_LINES)
		.map((block) => shorten(block.node.textContent))
		.filter(Boolean);

const buildZigzag = (range: CollapsedRange, lines: string[]) => {
	const element = document.createElement("div");
	element.className = ZIGZAG_CLASS;
	element.dataset.firstIndex = String(range.firstIndex);
	element.contentEditable = "false";
	element.style.setProperty("--diff-preview-delay", `${DIFF_ONLY_CHANGES.previewDelayMs}ms`);

	const label = document.createElement("span");
	label.className = "diff-collapsed-label";
	label.textContent = collapsedLabel(range.blocks);
	element.appendChild(label);

	if (lines.length) {
		const preview = document.createElement("div");
		preview.className = "diff-collapsed-preview";

		const caption = document.createElement("div");
		caption.className = "diff-collapsed-caption";
		caption.textContent = t("diff.only-changes.collapsed");
		preview.appendChild(caption);

		lines.forEach((line) => {
			const row = document.createElement("div");
			row.textContent = line;
			preview.appendChild(row);
		});

		element.appendChild(preview);
	}

	return element;
};

const decorationsFor = (doc: Node, ranges: CollapsedRange[]): DecorationSet => {
	if (!ranges.length) return DecorationSet.empty;

	const blocks = topLevelBlocks(doc);
	const decorations: Decoration[] = [];

	ranges.forEach((range) => {
		for (let index = range.firstIndex; index <= range.lastIndex; index++) {
			const block = blocks[index];
			if (block) decorations.push(Decoration.node(block.from, block.to, { class: HIDDEN_CLASS }));
		}

		const lines = previewOf(blocks, range);
		decorations.push(
			Decoration.widget(range.from, () => buildZigzag(range, lines), {
				side: -1,
				// The key carries the group size, or ProseMirror reuses the widget with a stale label.
				key: `collapsed-${range.firstIndex}-${range.blocks}`,
			}),
		);
	});

	return DecorationSet.create(doc, decorations);
};

const geometryOf = (stops: ChangeStop[], keepVisible: number[]) =>
	`${stops.map((stop) => stop.pos).join(",")}|${keepVisible.join(",")}`;

const followEdit = (
	state: OnlyChangesState,
	tr: Transaction,
	oldDoc: Node,
	newState: EditorState,
): OnlyChangesState => {
	const oldBlocks = topLevelBlocks(oldDoc);
	const newBlocks = topLevelBlocks(newState.doc);
	const mapIndex = (index: number) => {
		const block = oldBlocks[index];
		return block ? blockIndexAt(newBlocks, tr.mapping.map(block.from + 1)) : index;
	};
	return {
		...state,
		stops: state.stops.map((stop) => ({ ...stop, pos: tr.mapping.map(stop.pos) })),
		keepVisible: state.keepVisible.map(mapIndex),
		expanded: state.expanded.map(mapIndex),
		caret: newState.selection.head,
	};
};

const rebuild = (state: OnlyChangesState, doc: Node): OnlyChangesState => {
	// An empty model would hide the whole article with no toggle left to turn the mode off.
	if (!state.enabled || !state.stops.length) return { ...state, ranges: [], decorations: DecorationSet.empty };

	const caretStop = state.caret === null ? [] : [{ pos: state.caret, isDeleted: false, order: state.caret }];
	const ranges = collapsedRanges({
		doc,
		stops: [...state.stops, ...caretStop],
		keepVisible: state.keepVisible,
	}).filter((range) => !state.expanded.includes(range.firstIndex));

	return { ...state, ranges, decorations: decorationsFor(doc, ranges) };
};

/** `coordsAtPos` on a hidden block yields an empty rect and looks forward from `range.from`. */
export const visiblePos = (state: EditorState, pos: number): number => {
	const onlyChanges = onlyChangesKey.getState(state);
	if (!onlyChanges?.enabled) return pos;

	const hidden = onlyChanges.ranges.find((range) => pos >= range.from && pos < range.to);
	return hidden ? Math.max(hidden.from - 1, 0) : pos;
};

// Steps apply one after another, so each one is checked against the ranges moved by the steps before it.
const touchesHidden = (tr: Transaction, ranges: CollapsedRange[]) =>
	tr.steps.some((step, index) => {
		const mapping = tr.mapping.slice(0, index);
		let touches = false;
		step.getMap().forEach((start, end) => {
			touches ||= ranges.some((range) => start < mapping.map(range.to, -1) && end > mapping.map(range.from, 1));
		});
		return touches;
	});

const outOfHidden = (state: EditorState, previousHead: number): Selection | null => {
	const ranges = onlyChangesKey.getState(state)?.ranges ?? [];
	const { selection } = state;
	if (!selection.empty) return null;
	const hidden = ranges.find((range) => selection.head > range.from && selection.head < range.to);
	if (!hidden) return null;
	const forward = selection.head >= previousHead;
	return Selection.near(state.doc.resolve(forward ? hidden.to : hidden.from), forward ? 1 : -1);
};

export const setOnlyChanges = (view: EditorView, value: boolean) => {
	view.dispatch(view.state.tr.setMeta(onlyChangesKey, { type: "enabled", value } satisfies OnlyChangesMeta));
};

const onlyChanges = () =>
	new Plugin<OnlyChangesState>({
		key: onlyChangesKey,
		state: {
			init: () => ({
				enabled: false,
				stops: [],
				keepVisible: [],
				expanded: [],
				caret: null,
				ranges: [],
				decorations: DecorationSet.empty,
			}),
			apply: (tr, previous, oldState, newState) => {
				const meta = tr.getMeta(onlyChangesKey) as OnlyChangesMeta | undefined;
				if (!meta && !tr.docChanged) return previous;

				let next = previous;
				// The model is recomputed after a debounce; until then it must follow the edit, or the visible
				// window slides off the edited block.
				if (tr.docChanged) next = followEdit(next, tr, oldState.doc, newState);

				// Reset expansions only on changed geometry: comment edits republish the same model.
				if (meta?.type === "model") {
					const sameGeometry =
						geometryOf(meta.stops, meta.keepVisible) === geometryOf(next.stops, next.keepVisible);
					next = {
						...next,
						stops: meta.stops,
						keepVisible: meta.keepVisible,
						expanded: sameGeometry ? next.expanded : [],
						caret: null,
					};
				}
				if (meta?.type === "enabled") next = { ...next, enabled: meta.value, expanded: [] };
				if (meta?.type === "expand") next = { ...next, expanded: [...next.expanded, meta.firstIndex] };

				return rebuild(next, newState.doc);
			},
		},
		props: {
			decorations(state) {
				return onlyChangesKey.getState(state)?.decorations;
			},
		},
		// Hidden text must stay untouched: a selection across it would swallow it and autosave would drop it.
		// Syncs without history (file reloads, baseline resets) are not edits and must pass.
		filterTransaction: (tr, state) => {
			const onlyChanges = onlyChangesKey.getState(state);
			if (!tr.docChanged || !onlyChanges?.enabled || tr.getMeta("addToHistory") === false) return true;
			return !touchesHidden(tr, onlyChanges.ranges);
		},
		appendTransaction: (transactions, oldState, newState) => {
			if (!onlyChangesKey.getState(newState)?.enabled) return null;
			if (!transactions.some((tr) => tr.selectionSet || tr.docChanged)) return null;
			const selection = outOfHidden(newState, oldState.selection.head);
			return selection ? newState.tr.setSelection(selection) : null;
		},
		view: (view) => {
			const onClick = (event: MouseEvent) => {
				if (event.button !== 0) return;

				const zigzag = (event.target as HTMLElement)?.closest<HTMLElement>(`.${ZIGZAG_CLASS}`);
				if (!zigzag) return;

				const firstIndex = Number(zigzag.dataset.firstIndex);
				if (Number.isNaN(firstIndex)) return;

				view.dispatch(
					view.state.tr.setMeta(onlyChangesKey, { type: "expand", firstIndex } satisfies OnlyChangesMeta),
				);
			};

			view.dom.addEventListener("click", onClick);

			return {
				destroy: () => view.dom.removeEventListener("click", onClick),
			};
		},
	});

export default onlyChanges;
